<script setup lang="ts">
    import { useAuthStore } from '@/stores/auth.store';
    import type { MenuItem, SidebarMenus } from '@/types/components/sidebar';
    import { HomeRouteName } from '@/types/router/name/home';
    import { PhArrowUDownLeft } from '@phosphor-icons/vue';
    import { onMounted, onUnmounted, ref, type Ref } from 'vue';
    import { useRoute } from 'vue-router';

    const authStore = useAuthStore();
    const props = defineProps<SidebarMenus>();

    const route = useRoute();

    const popperVisible = ref(false);
    // Popper state
    const isPopperVisible = ref(false);
    const isPopperPinned = ref(false);

    const popperReference: Ref<(HTMLElement | null)[]> = ref([]);
    const popperContent = ref<HTMLElement | null>(null);

    // Popper handlers
    const hidePopper = () => {
        popperVisible.value = false;
    };

    const showPopper = (item: MenuItem) => {
        if (item.group === 'course') {
            isPopperVisible.value = true;
        }
    };

    const togglePopper = (item: MenuItem) => {
        if (item.group === 'course') {
            if (isPopperPinned.value) {
                isPopperPinned.value = false;
                isPopperVisible.value = false;
            } else {
                isPopperPinned.value = true;
                isPopperVisible.value = true;
            }
        } else {
            isPopperPinned.value = false;
            isPopperVisible.value = false;
        }
    };

    const handleClickOutside = (event: Event) => {
        const target = event.target as Node;

        const clickedReference = popperReference.value.some(ref => ref?.contains(target));

        const clickedContent = popperContent.value?.contains(target);

        if (!clickedReference && !clickedContent) {
            isPopperVisible.value = false;
            isPopperPinned.value = false;
        }
    };

    const active = (item: MenuItem) => item.group === route.meta.group;

    const subActive = (subItem: { sub: string }) => {
        return route.meta.sub === subItem.sub;
    };

    onMounted(() => {
        document.addEventListener('click', handleClickOutside);
    });

    onUnmounted(() => {
        document.removeEventListener('click', handleClickOutside);
    });
</script>

<template>
    <div class="flex flex-col gap-2 items-center justify-center">
        <div class="flex-center py-1" v-if="props.settings">
            <div
                @click="$router.push({ name: HomeRouteName.Home })"
                class="cursor-pointer text-brand-500 hover:bg-brand-50 rounded-lg w-12 h-12 flex-center transition-colors duration-200"
            >
                <PhArrowUDownLeft size="24" />
            </div>
        </div>

        <div class="flex-center pt-1 pb-3.5" v-else>
            <IconsCloseLogo
                class="cursor-pointer"
                @click="$router.push({ name: HomeRouteName.Home })"
            />
        </div>

        <div class="flex flex-col gap-2 relative">
            <template v-if="props.defaultMenu?.length">
                <router-link
                    v-for="item in props.defaultMenu"
                    v-show="authStore.canAccess(item.permission)"
                    :key="item.path"
                    :class="[
                        active(item) ? 'bg-brand-50 text-brand-500' : 'text-neutral-800',
                        'relative py-2.5 px-6 transition-colors duration-200 hover:bg-brand-50'
                    ]"
                    :to="item.path"
                    :title="item.label"
                >
                    <div class="flex-center relative">
                        <component
                            :is="item.icon"
                            class="mr-3"
                            :size="24"
                            :weight="active(item) ? 'fill' : 'regular'"
                        />
                    </div>
                    <div
                        v-if="active(item)"
                        class="absolute right-0 top-[9%] bottom-[9%] w-1 bg-brand-500 rounded-l-2xl"
                    ></div>
                </router-link>
            </template>

            <template v-if="props.subMenu?.length">
                <div
                    v-for="(item, i) in props.subMenu"
                    :key="item.path"
                    v-show="authStore.canAccess(item.permission)"
                    :class="[
                        active(item) ? 'bg-brand-50 text-brand-500' : 'text-neutral-800',
                        'relative py-5 px-[26px] transition-colors duration-200 hover:bg-[#CCE6E9] cursor-pointer'
                    ]"
                    :title="item.label"
                    @mouseenter="showPopper(item)"
                    @mouseleave="hidePopper"
                    @click="togglePopper(item)"
                    :ref="el => (popperReference[i] = el as HTMLElement | null)"
                >
                    <div class="flex-center relative">
                        <component
                            :is="item.icon"
                            class="mr-3"
                            :size="24"
                            :weight="active(item) ? 'fill' : 'regular'"
                        />
                    </div>
                    <div
                        v-if="active(item)"
                        class="absolute right-0 top-[9%] bottom-[9%] w-1 bg-brand-500 rounded-l-2xl"
                    ></div>

                    <div class="relative">
                        <div
                            v-if="isPopperVisible && item.group === 'course'"
                            class="popper-content"
                            :ref="el => (popperContent = el as HTMLElement | null)"
                            @mouseenter="showPopper(item)"
                            @mouseleave="hidePopper"
                        >
                            <!-- Sub menu items -->
                            <b class="text-neutral-800">Course</b>
                            <div class="flex flex-col gap-1 mt-1" v-if="item.child">
                                <router-link
                                    v-for="subItem in item.child"
                                    :key="subItem.path"
                                    :to="subItem.path"
                                    class="px-3 py-2 rounded transition-colors hover:bg-brand-50"
                                    :class="
                                        subActive(subItem)
                                            ? 'bg-brand-50 text-brand-500'
                                            : 'bg-white text-neutral-800'
                                    "
                                >
                                    <article>
                                        {{ subItem.label }}
                                    </article>
                                </router-link>
                            </div>

                            <div class="popper-arrow"></div>
                        </div>
                    </div>
                </div>
            </template>

            <!-- <span
                v-if="userStore.count > 0 && props.settings"
                class="bg-error-300 rounded-full w-4 h-4 justify-center absolute flex left-[43.5px] top-[174px] animate-ping"
            ></span>
            <span
                class="bg-error-100 rounded-full text-error-600 w-6 h-6 justify-center absolute flex left-10 top-[170px]"
                v-if="userStore.count > 0 && props.settings"
            >
                <b class="text-xs mt-1 ml-0.5">{{
                    userStore.count > 9 ? '9+' : userStore.count
                }}</b>
            </span> -->
        </div>
    </div>
</template>

<style scoped>
    .popper-content {
        position: absolute;
        top: 100%;
        left: 65px;
        transform: translateY(-50%) translateX(12px);
        background-color: white;
        border-radius: 0.5rem;
        box-shadow:
            0 4px 6px rgba(0, 0, 0, 0.1),
            0 1px 3px rgba(0, 0, 0, 0.08);
        padding: 8px 12px;
        min-width: 220px;
        max-width: 280px;
        z-index: 50;
        border: 1px solid #e5e7eb;
        animation: popperFadeIn 0.2s ease-out;
    }

    .popper-arrow {
        position: absolute;
        left: -7px;
        top: 50%;
        transform: translateY(-50%);
        width: 0;
        height: 0;
        border-top: 8px solid transparent;
        border-bottom: 8px solid transparent;
        border-right: 8px solid white;
    }

    .fade-slide-enter-active,
    .fade-slide-leave-active {
        transition: all 0.1s ease;
    }
    .fade-slide-enter-from {
        opacity: 0;
        transform: translateY(-10px);
    }
    .fade-slide-enter-to {
        opacity: 1;
        transform: translateY(0);
    }
    .fade-slide-leave-from {
        opacity: 1;
        transform: translateY(0);
    }
    .fade-slide-leave-to {
        opacity: 0;
        transform: translateY(-10px);
    }

    @keyframes popperFadeIn {
        from {
            opacity: 0;
            transform: translateY(-50%) translateX(8px);
        }
        to {
            opacity: 1;
            transform: translateY(-50%) translateX(12px);
        }
    }
</style>
